import React, { useMemo } from 'react';

import { isAnnounced, isFundRaising, Reservation } from 'api-client';
import { Money as MoneyModel } from 'api-client/models/common/Money';
import { CustomLink } from 'components/common';
import { PrimaryButton } from 'components/common/buttons';
import { PrimaryButtonColor } from 'components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import { formatPercents } from 'formatters';
import { useProfileData } from 'hooks';
import { RouteDictionary } from 'utils';

import styles from './useMyReservationContent.module.scss';

export const useMyReservationContent = (reservation: Reservation) => {
  const profileData = useProfileData();
  const project = reservation.project!;
  const accounting = project.context.accounting;

  const balanceAmount = profileData.balance?.amount ?? 0;
  const hasFunds = balanceAmount >= Number(reservation.money.amount);

  const money = useMemo(
    () => new MoneyModel(Number(reservation.money.amount), reservation.money.currencyCode),
    [reservation]
  );

  const roiIrr = `${formatPercents(accounting.simpleInterestRate, 0)} / ${formatPercents(
    accounting.interestRate,
    0
  )}`;

  const action = useMemo(() => {
    if (isAnnounced(project.status)) {
      return (
        <span className={styles.hint}>
          Подтвердите резервирование когда проект будет в статусе сбор средств
        </span>
      );
    }

    if (isFundRaising(project.status) && hasFunds) {
      return (
        <PrimaryButton color={PrimaryButtonColor.GREEN} className={styles.confirmButton}>
          <CustomLink to={RouteDictionary.DETAILED_PROJECT} params={{ uuid: project.uuid }}>
            Подтвердить сделку
          </CustomLink>
        </PrimaryButton>
      );
    }

    return <span className={styles.hint}>Пополните счет чтобы подтвердить резервирование</span>;
  }, [project, hasFunds]);

  return { project, accounting, money, roiIrr, hasFunds, action };
};
